import { FC, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Grid, MenuItem, Typography, } from '@material-ui/core';
import { InputField } from 'src/components/input-field';
import { MenuProps } from 'src/components/masters/customCss';
import { MAX_DAYS_FOR_APPOINTMENT, REPAIR_SERVICE_ID, REQUESTS_FROM, SOURCE_OF_INFORMATION } from 'src/constants/repairRequest-constants';
import { ServiceTypeModel } from 'src/interfaces/masters/service/service';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { Status, VerifiedStatus } from 'src/enums/common';
import { serviceMasterApi } from 'src/api/serviceType';
import { ResponseCodes } from 'src/enums/responseCodes';
import { ServicePriceMasterView, ServicePriceQuery } from 'src/interfaces/masters/Price/price';
import loadingContext from 'src/loderContext';
import { GetUserProductResponse } from 'src/interfaces/userProduct/user-product';
import { CustomerAddress } from 'src/interfaces/new-channel-partner/channelPartner';
import { getDateReverseFormat } from 'src/utils/date-format';
import { Availability } from 'src/interfaces/new-technician/technician';
import toast from 'react-hot-toast';
import { channelPartnerApi } from 'src/api/newChannelPartner/channelPartner';
import { productApi } from 'src/api/products';
import { servicePriceApi } from 'src/api/servicePrice';

interface RaiseRequestBasicInfoCardProps {
  selectedAddress: CustomerAddress;
  productDetails: GetUserProductResponse;
  setServicePrice: (data: ServicePriceMasterView) => void;
  setSlots: (data: Availability[]) => void;
  setCpId: (data: string) => void;
  setServiceType: (data: string) => void;
  setRequestFrom: (data: string) => void;
  setSourceOfInfo: (data: string) => void;
}

export const RaiseRequestBasicInfoCard: FC<RaiseRequestBasicInfoCardProps> = (props) => {
  const { selectedAddress, productDetails, setServicePrice, setCpId,
    setServiceType, setSlots, setRequestFrom, setSourceOfInfo } = props;
  const { setLoading } = useContext(loadingContext);
  const [allServices, setAllServices] = useState<ServiceTypeModel[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<string>('');
  const [channelPartnerId, setChannelPartnerId] = useState<string>('');
  const [servicePriceData,
    setServicePriceData] = useState<ServicePriceMasterView>();

  const getAllServices = async () => {
    const paginationObject: PaginationAndFilter = {
      filters: [
        {
          property: 'status',
          operator: 'in',
          value: [
            Status.PUBLISH,
          ],
        },
      ],
      pageSize: 99,
      pageNumber: 1,
      sort: -1,
      sortBy: ''
    };
    const serviceResponse = await serviceMasterApi.getServiceByPaginationAndFilters(
      paginationObject
    );
    if (serviceResponse.statusCode === ResponseCodes.OK) {
      setAllServices(serviceResponse?.responseData?.records);
    }
  };

  useEffect(() => {
    getAllServices().catch(console.error);
  }, []);

  const addDays = (days : number): Date => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);
    return futureDate;
  };

  const getChannelPartnerSlots = async (id: string) => {
    setLoading(true);
    const data = {
      fromDate: getDateReverseFormat(new Date()),
      toDate: getDateReverseFormat(addDays(MAX_DAYS_FOR_APPOINTMENT))
    };
    const requestResult = await channelPartnerApi.getChannelPartnerAvailabilitySlots(
      id, data
    );
    if (requestResult.statusCode === ResponseCodes.OK) {
      if (requestResult?.responseData?.length > 0) {
        setSlots(requestResult?.responseData);
      }
    } else {
      toast.error('Something went Wrong');
    }
    setLoading(false);
  };

  const getServicePriceDetails = async (categoryId: string) => {
    setLoading(true);
    const params: ServicePriceQuery = {
      serviceId: selectedServiceType,
      categoryId,
      productId: productDetails?.product?.id,
      productType: productDetails?.productType,
      brandId: productDetails?.brand?.id
    };
    const result = await servicePriceApi.getServicePriceDetail(params);
    if (result?.statusCode === ResponseCodes.OK) {
      setServicePriceData(result?.responseData);
      setServicePrice(result?.responseData);
    } else {
      setServicePriceData({} as ServicePriceMasterView);
    }
    setLoading(false);
  };

  const getProductDetailsById = async () => {
    setLoading(true);
    const getProductResult = await productApi.getProductById(productDetails?.product?.id);
    if (getProductResult?.id) {
      await getServicePriceDetails(getProductResult?.categoryId);
    }
    setLoading(false);
  };

  const checkChannelPartner = async () => {
    if (selectedAddress?.id) {
      if (productDetails?.id) {
        setLoading(true);
        setChannelPartnerId('');
        const paginationObject: PaginationAndFilter = {
          filters: [
            {
              property: 'status',
              operator: 'in',
              value: [
                VerifiedStatus.ACTIVE,
              ],
            },
            {
              property: 'serviceAreaIds',
              operator: 'in',
              value: [
                `${selectedAddress?.pincode}#${selectedAddress?.area}`,
              ],
            },
            {
              operator: 'equal',
              property: 'brandsProducts.products.key',
              value: `${productDetails?.brand?.id}-${productDetails?.product?.id}`,
            },
          ],
          pageSize: 999,
          pageNumber: 1,
        };
        const result = await channelPartnerApi
          .getChannelPartnersByPaginationAndFilters(paginationObject);
        if (result.statusCode === ResponseCodes.OK) {
          if (result?.responseData?.response?.records?.length > 0) {
            setCpId(result?.responseData?.response?.records[0]?.id);
            getChannelPartnerSlots(result?.responseData?.response?.records[0]?.id);
            setChannelPartnerId(result?.responseData?.response?.records[0]?.id);
          } else {
            setCpId('');
            setChannelPartnerId('');
          }
        }
        setLoading(false);
      }
    }
  };

  const servicePriceCheck = () => selectedAddress?.id
  && selectedServiceType === REPAIR_SERVICE_ID
  && productDetails && channelPartnerId && !servicePriceData?.id;

  useEffect(() => {
    if (selectedAddress?.id && productDetails?.id
      && selectedServiceType === REPAIR_SERVICE_ID) {
      checkChannelPartner().catch(console.error);
      getProductDetailsById().catch(console.error);
    }
  }, [selectedAddress?.id, productDetails?.id, selectedServiceType]);

  return (
    <>
      <Card
        variant="outlined"
      >
        <CardContent
          sx={{
            px: 2
          }}
        >
          <Grid
            container
            px={2}
            spacing={2}
          >
            <Grid
              item
              md={3}
              sm={6}
              xs={12}
            >
              <Typography
                display="flex"
                variant="subtitle2"
              >
                ServiceType
                <Typography
                  color="error.main"
                >
                  &nbsp;
                  *
                </Typography>
              </Typography>
            </Grid>
            <Grid
              item
              md={3}
              sm={6}
              xs={12}
            >
              <InputField
                placeholder="Service Type"
                name="selectedServiceType"
                required
                fullWidth
                onChange={(e) => {
                  setSelectedServiceType(e.target.value);
                  setServiceType(e.target.value);
                }}
                select
                sx={{ mb: 2 }}
                SelectProps={{ MenuProps }}
              >
                {allServices?.map((service) => (
                  <MenuItem
                    key={service.id}
                    value={service.id}
                  >
                    {service.type}
                  </MenuItem>
                ))}
              </InputField>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            px={2}
          >
            <Grid
              item
              md={3}
              sm={6}
              xs={12}
            >
              <Typography
                display="flex"
                variant="subtitle2"
              >
                Source Information
                <Typography
                  color="error.main"
                >
                  &nbsp;
                  *
                </Typography>
              </Typography>
            </Grid>
            <Grid
              item
              md={3}
              sm={6}
              xs={12}
            >
              <InputField
                fullWidth
                select
                name=""
                onChange={(e) => setSourceOfInfo(e.target.value)}
                required
                sx={{ mb: 2 }}
              >
                {SOURCE_OF_INFORMATION?.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                  >
                    {option}
                  </MenuItem>
                ))}
              </InputField>
            </Grid>
            <Grid
              item
              md={3}
              sm={6}
              xs={12}
            >
              <Typography
                display="flex"
                variant="subtitle2"
              >
                Request From
                <Typography
                  color="error.main"
                >
                  &nbsp;
                  *
                </Typography>
              </Typography>
            </Grid>
            <Grid
              item
              md={3}
              sm={6}
              xs={12}
            >
              <InputField
                fullWidth
                placeholder="Request From"
                select
                onChange={(e) => setRequestFrom(e.target.value)}
                required
                sx={{ mb: 2 }}
              >
                {REQUESTS_FROM?.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                  >
                    {option}
                  </MenuItem>
                ))}
              </InputField>
            </Grid>
          </Grid>
          <Grid
            container
          >
            <Grid
              item
              md={12}
              ml={2}
              sm={12}
              xs={12}
            >
              {!selectedAddress?.id && selectedServiceType === REPAIR_SERVICE_ID && (
              <Typography
                variant="body1"
                color="error.main"
              >
                Please select Address
              </Typography>
              )}
              {selectedAddress?.id && selectedServiceType === REPAIR_SERVICE_ID
            && productDetails?.id && channelPartnerId === '' && (
            <Typography
              variant="body1"
              color="error.main"
            >
              Channel Partner/Technician not available
              in this Area
            </Typography>
              )}
              {servicePriceCheck() && (
              <Typography
                variant="body1"
                color="error.main"
              >
                Service Price Not Available for this Product
              </Typography>
              )}
            </Grid>
          </Grid>

        </CardContent>
      </Card>
    </>
  );
};

RaiseRequestBasicInfoCard.propTypes = {
  // @ts-ignore
  selectedAddress: PropTypes.object,
  // @ts-ignore
  productDetails: PropTypes.object,
  // @ts-ignore
  setServicePrice: PropTypes.func,
  setSlots: PropTypes.func,
  setServiceType: PropTypes.func,
  setRequestFrom: PropTypes.func,
  setSourceOfInfo: PropTypes.func,
  setCpId: PropTypes.func,
};
